<template>
  <ui-modal-layout :title="tr('Ad account spending limit')" :icon="GaugeIcon">
    <template v-if="hasExistingCap">
      <div class="flex flex-col gap-1 rounded-lg border border-border p-4">
        <span class="text-base font-semibold text-foreground">
          {{ tr('Current spend') }}: {{ formatPrice(amountSpent, currencySymbol, null, true) }}
        </span>
        <span class="text-sm text-muted-foreground">
          {{ tr('Remaining') }}: <span class="font-semibold text-foreground">{{ formatPrice(remaining, currencySymbol, null, true) }}</span>
        </span>
      </div>

      <info-text>
        {{ tr('Spending counts within a monthly cycle that Meta manages (typically resetting at the start of the month). The exact reset date is shown in Ads Manager.') }}
        <a
          v-if="adsManagerBillingUrl"
          :href="adsManagerBillingUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-1 block"
        >
          {{ tr('Open in Ads Manager') }}
        </a>
      </info-text>
    </template>

    <div class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Spending limit') }}</ui-label>
      <p class="m-0 text-xs text-muted-foreground">
        {{ tr('The maximum your ad account can spend in total. When it is reached, Meta pauses all delivery on the account.') }}
      </p>
      <money-input v-model="inputValue" :currency="currency" :min="0" />
    </div>

    <info-text v-if="belowSpent" type="error">
      {{ tr('The spending limit must be at least the amount already spent (:amount).', { ':amount': formatPrice(amountSpent, currencySymbol, null, true) }) }}
    </info-text>
    <info-text>
      {{ tr('This is a Meta ad account setting. It applies to the whole ad account, so it affects every brand that uses it.') }}
    </info-text>

    <ui-button
      v-if="hasExistingCap"
      variant="ghost"
      class="self-start text-destructive hover:text-destructive"
      :disabled="isSaving || isRemoving"
      @click="() => removeLimit()"
    >
      <loader-circle-icon v-if="isRemoving" class="animate-spin" />
      {{ tr('Remove spending limit') }}
    </ui-button>

    <template #footer>
      <ui-button variant="outline" :disabled="isSaving || isRemoving" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button :disabled="isSaving || isRemoving || !canSave" @click="() => save()">
        <loader-circle-icon v-if="isSaving" class="animate-spin" />
        {{ tr('Save') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { Gauge, LoaderCircle } from '@lucide/vue';
import { markRaw } from 'vue';

import AdAccountService from '@/services/AdAccount.js';
import InfoText from '@/ui/components/InfoText.vue';
import MoneyInput from '@/ui/components/MoneyInput.vue';
import { formatPrice } from '@/utils/number.js';

export default {
  name: 'AdAccountSpendCapModalContent',
  components: {
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    MoneyInput,
  },
  props: {
    adAccount: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      GaugeIcon: markRaw(Gauge),
      isSaving: false,
      isRemoving: false,
      inputValue: this.adAccount.spend_cap ?? 0,
    };
  },
  computed: {
    currency() {
      return this.adAccount.currency;
    },
    spendCap() {
      return this.adAccount.spend_cap ?? 0;
    },
    hasExistingCap() {
      return this.spendCap > 0;
    },
    amountSpent() {
      if (!this.hasExistingCap) {
        return 0;
      }

      return this.adAccount.amount_spent ?? 0;
    },
    adAccountId() {
      return this.adAccount.id;
    },
    externalId() {
      return this.adAccount.external_id;
    },
    currencySymbol() {
      return this.adAccount.currencySymbol;
    },
    remaining() {
      return Math.max(0, (this.spendCap || 0) - (this.amountSpent || 0));
    },
    belowSpent() {
      return this.inputValue < this.amountSpent;
    },
    canSave() {
      return !!this.inputValue && this.inputValue > 0 && !this.belowSpent;
    },
    adsManagerBillingUrl() {
      if (!this.externalId) {
        return null;
      }

      const numericId = String(this.externalId).replace('act_', '');
      return 'https://business.facebook.com/ads/manager/account_settings/account_billing/?act=' + numericId;
    },
  },
  methods: {
    formatPrice,
    close() {
      this.$emit('close');
    },
    save() {
      if (this.isSaving || this.isRemoving || !this.canSave) {
        return;
      }

      this.$toast.clear();
      this.isSaving = true;

      AdAccountService.updateSpendCap(this.adAccountId, this.inputValue)
        .then(payload => {
          this.adAccount.updateWith(payload);
          this.$toast.success(this.tr('Spending limit saved.'));
          this.close();
          return payload;
        })
        .catch(err => this.error(err))
        .finally(() => this.isSaving = false);
    },
    removeLimit() {
      if (this.isSaving || this.isRemoving) {
        return;
      }

      this.$toast.clear();
      this.isRemoving = true;

      AdAccountService.deleteSpendCap(this.adAccountId)
        .then(payload => {
          this.adAccount.updateWith(payload);
          this.$toast.success(this.tr('Spending limit removed.'));
          this.close();
          return payload;
        })
        .catch(err => this.error(err))
        .finally(() => this.isRemoving = false);
    },
  },
};
</script>
